import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../lib/config';
import { identifyUser, track } from '../lib/analytics';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [keepSignedIn, setKeepSignedIn] = useState(false);

  async function handleLogin() {
    setError(null);

    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Login failed. Please try again.');
        setLoading(false);
        return;
      }

      // Store auth — localStorage if keep signed in, sessionStorage otherwise
      const store = keepSignedIn ? localStorage : sessionStorage;
      store.setItem('carpeUserId', data.user_id);
      store.setItem('carpeUserEmail', data.email);
      store.setItem('carpeAccessToken', data.access_token);
      if (keepSignedIn && data.refresh_token) {
        localStorage.setItem('carpeRefreshToken', data.refresh_token);
        localStorage.setItem('carpeKeepSignedIn', 'true');
      }
      identifyUser(data.user_id);
      track('Login');

      // Check if they have a saved profile
      const profileRes = await fetch(
        `${API_BASE}/auth/profile/${data.user_id}`
      );
      const profileData = await profileRes.json();

      if (profileData.profile) {
        // Returning user - map Supabase profile fields to app format
        const p = profileData.profile;
        const mappedProfile = {
          profile: p.traveler_profile,
          explorer_score: p.explorer_score,
          pace: p.pace,
          activity_level: p.activity_level,
          social_vibe: p.social_vibe,
          environment_tags: p.environment_tags,
          best_for_tags: p.best_for_tags || [],
          mix: p.mix,
          slot_counts: p.slot_counts,
          activity_profile: p.activity_profile,
          activity_dimensions: p.activity_dimensions,
          activity_profile_description: p.activity_profile_description,
          wanderlust_signal: p.wanderlust_signal,
          children_flag: p.children_flag,
          family_micro_answers: p.family_micro_answers,
          profile_short: p.profile_short,
          profile_long: p.profile_long,
        };
        sessionStorage.setItem('carpeWonderProfile', JSON.stringify(mappedProfile));
        if (keepSignedIn) {
          localStorage.setItem('carpeWonderProfile', JSON.stringify(mappedProfile));
        }

        // Try to load their saved destinations
        const destRes = await fetch(
          `${API_BASE}/auth/destinations/latest/${data.user_id}`
        );
        const destData = await destRes.json();

        if (destData.destinations) {
          const allRanked = destData.destinations.destinations || [];
          const page1Dests = allRanked.slice(0, 5);
          const enrichTeasers = destData.destinations.enrich_teasers || {};
          const enrichFacts = destData.destinations.enrich_facts || {};

          // Hydrate full ranked pool so pagination works on return
          sessionStorage.setItem('carpeRankedPool', JSON.stringify(allRanked));

          // First 5 destinations are fully enriched (match_reason already included
          // from when they were saved) — use directly as carpePaginationPool.page1.
          // Remaining 25 go into carpeRankedPool for on-demand pagination.
          // Restore full pagination pool from Supabase if available,
          // otherwise reconstruct with just page 1
          const savedPool = destData.destinations.pagination_pool || {};
          const paginationPool = Object.keys(savedPool).length > 0
            ? savedPool
            : { page1: page1Dests };
          sessionStorage.setItem('carpePaginationPool', JSON.stringify(paginationPool));
          // Store the row ID so update-pool PATCH targets the correct row
          sessionStorage.setItem('carpeCurrentSaveId', destData.destinations.id);
          sessionStorage.setItem('carpeSavedDestinations', JSON.stringify(page1Dests));
          sessionStorage.setItem('carpeEnrichTeasers', JSON.stringify(enrichTeasers));
          sessionStorage.setItem('carpeEnrichFacts', JSON.stringify(enrichFacts));
          sessionStorage.setItem('carpeReturningUser', 'true');

          if (keepSignedIn) {
            localStorage.setItem('carpeRankedPool', JSON.stringify(allRanked));
            localStorage.setItem('carpeSavedDestinations', JSON.stringify(page1Dests));
            localStorage.setItem('carpeEnrichTeasers', JSON.stringify(enrichTeasers));
            localStorage.setItem('carpeEnrichFacts', JSON.stringify(enrichFacts));
          }
        }

        navigate('/welcome');
      } else {
        // New user - take the quiz
        navigate('/quiz');
      }
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.logo}>Carpe Wonder™</div>

        <h1 style={styles.headline}>Welcome back</h1>
        <p style={styles.subline}>
          Sign in to see your destinations and traveler profile.
        </p>

        <div style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Your password"
              style={styles.input}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}
          <div style={styles.checkboxRow}>
            <input
              type="checkbox"
              id="keepSignedIn"
              checked={keepSignedIn}
              onChange={e => setKeepSignedIn(e.target.checked)}
              style={styles.checkbox}
            />
            <label htmlFor="keepSignedIn" style={styles.checkboxLabel}>
              Keep me signed in
            </label>
          </div>
          <button

            style={{
              ...styles.button,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in →'}
          </button>
          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <button
              style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontSize: '13px' }}
              onClick={() => navigate('/forgot-password')}
            >
              Forgot password?
            </button>
          </div>
        </div>
        <div style={styles.switchRow}>
          No account yet?{' '}
          <button style={styles.switchBtn} onClick={() => navigate('/register')}>
            Create one
          </button>
        </div>

        <button style={styles.backBtn} onClick={() => navigate('/')}>
          ← Back to home
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0d0d1f',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '0 16px',
  },
  container: {
    width: '100%',
    maxWidth: '400px',
    paddingTop: '48px',
    paddingBottom: '48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    fontSize: '14px',
    color: 'var(--gold)',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    marginBottom: '32px',
  },
  headline: {
    fontSize: '26px',
    fontWeight: '500',
    color: '#fff',
    textAlign: 'center',
    marginBottom: '10px',
    lineHeight: '1.2',
  },
  subline: {
    fontSize: '15px',
    color: 'rgba(255,255,255,0.65)',
    textAlign: 'center',
    lineHeight: '1.6',
    marginBottom: '32px',
    maxWidth: '300px',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.65)',
    letterSpacing: '0.05em',
  },
  input: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '10px',
    padding: '12px 14px',
    fontSize: '15px',
    color: '#fff',
    outline: 'none',
    width: '100%',
    minHeight: '44px',
  },
  error: {
    fontSize: '14px',
    color: '#e07b6b',
    textAlign: 'center',
    padding: '12px',
    background: 'rgba(224,123,107,0.1)',
    borderRadius: '8px',
    lineHeight: '1.5',
  },
  button: {
    width: '100%',
    background: 'var(--gold)',
    border: '2px solid rgba(255,255,255,0.8)',
    borderRadius: '28px',
    padding: '16px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#0d0d1f',
    marginTop: '8px',
    minHeight: '44px',
  },
  switchRow: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.6)',
    marginBottom: '16px',
  },
  switchBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--gold)',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '4px',
    minHeight: '44px',
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: 'rgba(255,255,255,0.55)',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px 0',
    minHeight: '44px',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: '#c9a84c',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.65)',
    cursor: 'pointer',
  },
};

export default Login;
